<template>
  <!-- Strict flex layout anchored to the screen bounds -->
  <div
    v-if="localSettings"
    class="absolute-full bg-grey-2"
    style="display: flex; flex-direction: column; overflow: hidden"
  >
    <!-- FIXED HEADER WITH TABS -->
    <div class="bg-white shadow-2" style="z-index: 10; flex: 0 0 auto">
      <q-tabs
        v-model="activeTab"
        dense
        class="text-grey-8"
        active-color="primary"
        indicator-color="primary"
        align="justify"
      >
        <q-tab name="general" label="General Workflow" icon="settings" />
        <q-tab name="dimensions" label="Play Dimensions" icon="analytics" />
        <q-tab name="scouting" label="Scouting" icon="radar" />
      </q-tabs>
    </div>

    <!-- SCROLLABLE TAB PANELS -->
    <q-tab-panels
      v-model="activeTab"
      animated
      class="bg-transparent"
      style="flex: 1 1 auto; min-height: 0; overflow-y: auto; overflow-x: hidden"
    >
      <!-- ========================================== -->
      <!-- TAB 1: GENERAL WORKFLOW -->
      <!-- ========================================== -->
      <q-tab-panel name="general" class="q-pa-md q-gutter-y-md">
        <!-- GAMEPLAY DEFAULTS -->
        <q-card flat bordered class="bg-white">
          <q-card-section class="bg-grey-1 border-bottom q-py-sm">
            <div class="text-subtitle2 text-weight-bold text-grey-8 text-uppercase">
              Game Defaults
            </div>
          </q-card-section>
          <q-card-section class="q-pb-sm">
            <div class="row q-col-gutter-md">
              <q-select
                v-model="localSettings.defaultInnings"
                :options="[6, 7, 9]"
                label="Default Innings (Scoreboard)"
                outlined
                dense
                class="col-12 col-sm-6"
                @update:model-value="saveSettings"
              />
              <q-select
                v-model="localSettings.defaultRosterSize"
                :options="[9, 10, 11, 12, 15]"
                label="Default Roster Size"
                outlined
                dense
                class="col-12 col-sm-6"
                @update:model-value="saveSettings"
              />
            </div>
          </q-card-section>

          <q-list separator>
            <q-item tag="label" v-ripple>
              <q-item-section>
                <q-item-label>Use Designated Hitter (DH)</q-item-label>
                <q-item-label caption>
                  Skip the Pitcher in the batting rotation if a DH is assigned to the lineup.
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-toggle
                  v-model="localSettings.useDesignatedHitter"
                  @update:model-value="saveSettings"
                />
              </q-item-section>
            </q-item>
          </q-list>
        </q-card>

        <!-- TRACKING WORKFLOW -->
        <q-card flat bordered class="bg-white">
          <q-card-section class="bg-grey-1 border-bottom q-py-sm">
            <div class="text-subtitle2 text-weight-bold text-grey-8 text-uppercase">
              Tracking Workflow
            </div>
          </q-card-section>
          <q-list separator>
            <q-item tag="label" v-ripple>
              <q-item-section>
                <q-item-label>Auto-Advance Lead Runner</q-item-label>
                <q-item-label caption
                  >Automatically opens the runner dialog after saving a batter's play if a runner is
                  on base.</q-item-label
                >
              </q-item-section>
              <q-item-section side>
                <q-toggle
                  v-model="localSettings.autoAdvanceLeadRunner"
                  @update:model-value="saveSettings"
                />
              </q-item-section>
            </q-item>
            <q-item tag="label" v-ripple>
              <q-item-section>
                <q-item-label>Readable Play Status</q-item-label>
                <q-item-label caption
                  >Show play history as human-readable text instead of strict Retrosheet
                  abbreviation codes (e.g. "Groundout" instead of "6-3/G").</q-item-label
                >
              </q-item-section>
              <q-item-section side>
                <q-toggle
                  v-model="localSettings.useReadablePlayStatus"
                  @update:model-value="saveSettings"
                />
              </q-item-section>
            </q-item>
          </q-list>
        </q-card>

        <!-- PITCHING DISPLAY OPTIONS-->
        <q-card flat bordered class="bg-white">
          <q-card-section class="bg-grey-1 border-bottom q-py-sm">
            <div class="text-subtitle2 text-weight-bold text-grey-8 text-uppercase">
              Pitching HUD Visibility
            </div>
          </q-card-section>
          <q-list separator>
            <q-item tag="label" v-ripple>
              <q-item-section>
                <q-item-label>Show Pitch Categories</q-item-label>
                <q-item-label caption
                  >Display the Primary (Ball, Strike, In-Play) and Sub-Category
                  selectors.</q-item-label
                >
              </q-item-section>
              <q-item-section side>
                <q-toggle
                  v-model="localSettings.enablePitchCategories"
                  @update:model-value="saveSettings"
                />
              </q-item-section>
            </q-item>
            <q-item tag="label" v-ripple>
              <q-item-section>
                <q-item-label>Show Pitch Types</q-item-label>
                <q-item-label caption
                  >Display the selection row for specific pitch types (e.g., Fastball,
                  Slider).</q-item-label
                >
              </q-item-section>
              <q-item-section side>
                <q-toggle
                  v-model="localSettings.enablePitchTypes"
                  @update:model-value="saveSettings"
                />
              </q-item-section>
            </q-item>
            <q-item tag="label" v-ripple>
              <q-item-section>
                <q-item-label>Show Pitch Quality</q-item-label>
                <q-item-label caption
                  >Display the pitch quality / location evaluation row.</q-item-label
                >
              </q-item-section>
              <q-item-section side>
                <q-toggle
                  v-model="localSettings.enablePitchQuality"
                  @update:model-value="saveSettings"
                />
              </q-item-section>
            </q-item>
          </q-list>
        </q-card>

        <!-- VISIBILITY TOGGLES -->
        <q-card flat bordered class="bg-white">
          <q-card-section class="bg-grey-1 border-bottom q-py-sm">
            <div class="text-subtitle2 text-weight-bold text-grey-8 text-uppercase">
              Menu Visiblity Options
            </div>
          </q-card-section>
          <q-card-section>
            <div class="text-caption text-weight-bold text-primary q-mb-sm">
              Runner Advance Options
            </div>
            <div class="row wrap q-gutter-sm q-mb-md">
              <q-btn
                v-for="opt in settingsStore._rawRunnerAdvanceOptions"
                :key="opt.value"
                :label="getCustomLabel(opt.value) || opt.label"
                :color="
                  !localSettings.hiddenRunnerOptions.includes(opt.value) ? 'primary' : 'grey-3'
                "
                :text-color="
                  !localSettings.hiddenRunnerOptions.includes(opt.value) ? 'white' : 'grey-7'
                "
                unelevated
                size="sm"
                class="text-weight-bold"
                @click="toggleArrayItem('hiddenRunnerOptions', opt.value)"
              />
            </div>

            <div class="text-caption text-weight-bold text-primary q-mb-sm">
              Advanced Pitch Options
            </div>
            <div class="row wrap q-gutter-sm">
              <q-btn
                v-for="opt in settingsStore._rawPitchAdvancedOptions"
                :key="opt.value"
                :label="getCustomLabel(opt.value) || opt.label"
                :color="
                  !localSettings.hiddenPitchOptions.includes(opt.value) ? 'primary' : 'grey-3'
                "
                :text-color="
                  !localSettings.hiddenPitchOptions.includes(opt.value) ? 'white' : 'grey-7'
                "
                unelevated
                size="sm"
                class="text-weight-bold"
                @click="toggleArrayItem('hiddenPitchOptions', opt.value)"
              />
            </div>
          </q-card-section>
        </q-card>
      </q-tab-panel>

      <!-- ========================================== -->
      <!-- TAB 2: FIELDING DIMENSIONS -->
      <!-- ========================================== -->
      <q-tab-panel name="dimensions" class="q-pa-md">
        <div class="text-caption text-grey-8 q-mb-md">
          Customize the fielding execution metrics tracked by your coaches. Disable entire
          dimensions to simplify the UI, or override default labels to match your program's
          terminology.
        </div>

        <q-list bordered class="bg-white rounded-borders">
          <q-expansion-item
            v-for="dim in dimensionCategories"
            :key="dim.key"
            group="dimensions"
            header-class="text-primary"
          >
            <template v-slot:header>
              <q-item-section>
                <div class="text-subtitle2 text-weight-bold text-uppercase">{{ dim.label }}</div>
              </q-item-section>
              <q-item-section side @click.stop>
                <q-toggle
                  :model-value="!localSettings.hiddenDimensions.includes(dim.key)"
                  @update:model-value="toggleArrayItem('hiddenDimensions', dim.key)"
                  color="positive"
                  label="Enable Category"
                  left-label
                  size="sm"
                />
              </q-item-section>
            </template>

            <q-separator />

            <q-list bordered class="bg-grey-1">
              <q-expansion-item
                v-for="situationGroup in dim.situationGroups"
                :key="dim.key + '-' + situationGroup.situation"
                :group="dim.key + '-situations'"
                header-class="text-grey-8"
                dense
              >
                <template v-slot:header>
                  <q-item-section>
                    <div class="text-caption text-weight-bold">{{ situationGroup.label }}</div>
                  </q-item-section>
                </template>

                <q-card class="bg-white">
                  <q-card-section>
                    <div v-if="situationGroup.options.length > 0">
                      <div
                        v-for="opt in situationGroup.options"
                        :key="opt.value"
                        class="row wrap items-center q-col-gutter-sm q-mb-sm"
                      >
                        <div class="col-12 col-sm-8">
                          <q-input
                            :model-value="localSettings.customLabels[String(opt.value)] || ''"
                            @update:model-value="
                              (val: string | number | null) =>
                                setCustomLabel(String(opt.value), val)
                            "
                            :placeholder="opt.label"
                            label="Custom Label Override"
                            :hint="
                              localSettings.customLabels[String(opt.value)]
                                ? `Active Override: ${localSettings.customLabels[String(opt.value)]}`
                                : `Default: ${opt.label}`
                            "
                            outlined
                            dense
                            bg-color="white"
                          >
                            <template v-slot:append>
                              <q-icon
                                v-if="localSettings.customLabels[String(opt.value)]"
                                name="close"
                                class="cursor-pointer"
                                size="xs"
                                @click="clearCustomLabel(String(opt.value))"
                              />
                            </template>
                          </q-input>
                          <div
                            v-if="sharedAcross(dim, opt.value).length > 1"
                            class="text-caption text-warning q-mt-xs"
                          >
                            <q-icon name="info" size="xs" />
                            Shared with:
                            {{
                              sharedAcross(dim, opt.value)
                                .filter((p) => p !== situationGroup.label)
                                .join(', ')
                            }}
                          </div>
                        </div>
                        <div class="col-12 col-sm-4">
                          <q-toggle
                            :model-value="!isHidden(dim.hiddenArray, opt.value)"
                            @update:model-value="toggleArrayItem(dim.hiddenArray, opt.value)"
                            label="Visible"
                            color="primary"
                            dense
                          />
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-grey-6 text-italic text-caption">
                      No predefined options for this position.
                    </div>
                  </q-card-section>
                </q-card>
              </q-expansion-item>
            </q-list>
          </q-expansion-item>
        </q-list>
      </q-tab-panel>

      <!-- ========================================== -->
      <!-- TAB 3: SCOUTING & NOTES (Extracted!) -->
      <!-- ========================================== -->
      <q-tab-panel name="scouting" class="q-pa-md">
        <!-- Render the new autonomous component -->
        <UserSettingsScouting />
      </q-tab-panel>
    </q-tab-panels>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useAuthStore } from 'stores/auth-store'
import { useSettingsStore } from 'stores/settings-store'
import type { UserSettings } from 'stores/auth-store-types'

// NEW: Import the extracted scouting editor
import UserSettingsScouting from 'components/UserSettingsScouting.vue' // <-- Adjust this path if needed

const authStore = useAuthStore()
const settingsStore = useSettingsStore()

const activeTab = ref('general')
const localSettings = ref<UserSettings | null>(null)

const defaultSettings: UserSettings = {
  defaultInnings: 6,
  defaultRosterSize: 11,
  useDesignatedHitter: true,
  themePreference: 'system',
  enablePitchQuickLog: true,
  enablePitchCategories: true,
  enablePitchTypes: true,
  enablePitchQuality: true,
  useReadablePlayStatus: false,
  autoAdvanceLeadRunner: true,
  hiddenPitchOptions: [],
  hiddenApproachOptions: [],
  hiddenExecutionOptions: [],
  hiddenTransferOptions: [],
  hiddenThrowOptions: [],
  hiddenDecisionOptions: [],
  hiddenQualityOptions: [],
  hiddenDimensions: [],
  hiddenRunnerOptions: [],
  hiddenScoutingTags: [],
  helpModeEnabled: true,
  customLabels: {},
  customCategories: {},
}

onMounted(async () => {
  if (authStore.currentUser) {
    const userSettings = authStore.currentUser.settings
    localSettings.value = {
      ...defaultSettings,
      ...(userSettings ? JSON.parse(JSON.stringify(userSettings)) : {}),
    }
    if (!userSettings || Object.keys(userSettings).length < Object.keys(defaultSettings).length) {
      await saveSettings()
    }
  }
})

// --- DIMENSION MAPPING ---
interface SituationGroup {
  situation: string
  label: string
  options: { value: string | number; label: string }[]
}

interface DimensionCategory {
  key: string
  label: string
  hiddenArray: keyof UserSettings
  situationGroups: SituationGroup[]
}

const dimensionCategories = computed<DimensionCategory[]>(() => [
  {
    key: 'approach',
    label: 'Range / Approach',
    hiddenArray: 'hiddenApproachOptions',
    situationGroups: [
      {
        situation: 'ground_ball',
        label: 'Ground Ball',
        options: settingsStore.primaryApproachOptionsGroundBall,
      },
      {
        situation: 'air_ball',
        label: 'Air Ball (Fly/Line/Pop)',
        options: settingsStore.primaryApproachOptionsAirBall,
      },
      {
        situation: 'receiving',
        label: 'Receiving a Throw',
        options: settingsStore.primaryApproachOptionsReceiving,
      },
      {
        situation: 'relay_decision',
        label: 'Relay / Cutoff',
        options: settingsStore.primaryApproachOptionsRelayDecision,
      },
      {
        situation: 'catcher_blocking',
        label: 'Catcher Blocking',
        options: settingsStore.primaryApproachOptionsCatcher,
      },
    ],
  },
  {
    key: 'execution',
    label: 'Fielding Execution',
    hiddenArray: 'hiddenExecutionOptions',
    situationGroups: [
      {
        situation: 'ground_ball',
        label: 'Ground Ball',
        options: settingsStore.primaryExecutionOptionsGroundBall,
      },
      {
        situation: 'air_ball',
        label: 'Air Ball (Fly/Line/Pop)',
        options: settingsStore.primaryExecutionOptionsAirBall,
      },
      {
        situation: 'receiving',
        label: 'Receiving a Throw',
        options: settingsStore.primaryExecutionOptionsReceiving,
      },
      {
        situation: 'relay_decision',
        label: 'Relay / Cutoff',
        options: settingsStore.primaryExecutionOptionsRelayDecision,
      },
      {
        situation: 'catcher_blocking',
        label: 'Catcher Blocking',
        options: settingsStore.primaryExecutionOptionsCatcher,
      },
    ],
  },
  {
    key: 'throw',
    label: 'Throw',
    hiddenArray: 'hiddenThrowOptions',
    situationGroups: [
      {
        situation: 'fielding',
        label: 'After Fielding',
        options: settingsStore.primaryThrowOptionsFielding,
      },
      {
        situation: 'receiving',
        label: 'Receiving a Throw',
        options: settingsStore.primaryThrowOptionsReceiving,
      },
      { situation: 'catcher', label: 'Catcher', options: settingsStore.primaryThrowOptionsCatcher },
      {
        situation: 'relay',
        label: 'Relay / Cutoff',
        options: settingsStore.primaryThrowOptionsRelay,
      },
    ],
  },
  {
    key: 'decision',
    label: 'Decision',
    hiddenArray: 'hiddenDecisionOptions',
    situationGroups: [
      {
        situation: 'general',
        label: 'General',
        options: settingsStore.primaryDecisionOptionsGeneral,
      },
      {
        situation: 'catcher',
        label: 'Catcher',
        options: settingsStore.primaryDecisionOptionsCatcher,
      },
    ],
  },
  {
    key: 'quality',
    label: 'Play Quality Rating',
    hiddenArray: 'hiddenQualityOptions',
    situationGroups: [
      {
        situation: 'all',
        label: 'All Situations',
        options: settingsStore._rawQualityOptions || [],
      },
    ],
  },
])

const sharedAcross = (dim: DimensionCategory, value: string | number): string[] => {
  return dim.situationGroups
    .filter((pg) => pg.options.some((o) => o.value === value))
    .map((pg) => pg.label)
}

const getCustomLabel = (key: string | number) => {
  return localSettings.value?.customLabels?.[String(key)] || ''
}

const isHidden = (arrayName: keyof UserSettings, value: string | number) => {
  if (!localSettings.value) return false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const arrayRef = localSettings.value[arrayName] as any[]
  return arrayRef ? arrayRef.includes(value) : false
}

const toggleArrayItem = async (arrayName: keyof UserSettings, value: string | number) => {
  if (!localSettings.value) return
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const arrayRef = localSettings.value[arrayName] as any[]

  const index = arrayRef.indexOf(value)
  if (index > -1) {
    arrayRef.splice(index, 1)
  } else {
    arrayRef.push(value)
  }
  await saveSettings()
}

const clearCustomLabel = async (key: string | number) => {
  if (localSettings.value && localSettings.value.customLabels) {
    delete localSettings.value.customLabels[String(key)]
    await saveSettings()
  }
}

const saveSettings = async () => {
  if (localSettings.value) {
    await authStore.updateSettings(localSettings.value)
  }
}

const setCustomLabel = async (key: string, val: string | number | null) => {
  if (!localSettings.value) return
  if (!val) {
    delete localSettings.value.customLabels[String(key)]
  } else {
    localSettings.value.customLabels[String(key)] = String(val)
  }
  await saveSettings()
}
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}
</style>
